// Settings page: account info, household management, theme picker, and sign-out.

import { useEffect, useState } from "react";
import { useAuth } from "../auth";
import { api } from "../api";
import { getTheme, setTheme, type Theme } from "../theme";
import type { User } from "../types";

export function Settings() {
  const { user, household, refresh, logout } = useAuth();
  const [members, setMembers] = useState<User[]>([]);
  const [householdName, setHouseholdName] = useState(household?.name ?? "");
  const [savingName, setSavingName] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [theme, setThemeState] = useState<Theme>(() => getTheme());

  useEffect(() => {
    void api
      .householdMembers()
      .then((r) => setMembers(r.members))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (household) setHouseholdName(household.name);
  }, [household]);

  function pickTheme(next: Theme) {
    setTheme(next);
    setThemeState(next);
  }

  async function saveName() {
    setSavingName(true);
    setErr(null);
    try {
      await api.updateHousehold({ name: householdName });
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed");
    } finally {
      setSavingName(false);
    }
  }

  async function rotate() {
    if (!confirm("Generate a new invite code? The old code will stop working.")) return;
    setRotating(true);
    try {
      await api.updateHousehold({ rotate_invite: true });
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed");
    } finally {
      setRotating(false);
    }
  }

  async function copyCode() {
    if (!household) return;
    try {
      await navigator.clipboard.writeText(household.invite_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  }

  if (!user || !household) return null;

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-4">
      <section className="card p-4">
        <h2 className="mb-3 text-sm font-semibold text-stone-700 dark:text-stone-300">Account</h2>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between">
            <dt className="text-stone-500 dark:text-stone-400">Name</dt>
            <dd>{user.name}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-stone-500 dark:text-stone-400">Email</dt>
            <dd className="truncate">{user.email}</dd>
          </div>
        </dl>
      </section>

      <section className="card p-4">
        <h2 className="mb-3 text-sm font-semibold text-stone-700 dark:text-stone-300">Appearance</h2>
        <div role="radiogroup" aria-label="Theme" className="grid grid-cols-3 gap-2">
          {(["system", "light", "dark"] as const).map((opt) => {
            const active = theme === opt;
            return (
              <button
                key={opt}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => pickTheme(opt)}
                className={`rounded-lg border px-3 py-3 text-sm font-medium transition-colors ${
                  active
                    ? "border-brand-600 bg-brand-50 text-brand-900 dark:bg-brand-900/30 dark:text-brand-200"
                    : "border-stone-200 bg-white text-stone-700 hover:border-stone-300 dark:border-stone-700 dark:bg-stone-900 dark:text-stone-300 dark:hover:border-stone-600"
                }`}
              >
                <ThemeIcon kind={opt} />
                <span className="mt-1 block capitalize">{opt}</span>
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-stone-500 dark:text-stone-400">
          {theme === "system"
            ? "Following your device setting."
            : theme === "dark"
            ? "Always dark."
            : "Always light."}
        </p>
      </section>

      <section className="card p-4">
        <h2 className="mb-3 text-sm font-semibold text-stone-700 dark:text-stone-300">Household</h2>
        <div className="space-y-3">
          <div>
            <label className="label">Name</label>
            <div className="flex gap-2">
              <input
                className="input flex-1"
                value={householdName}
                onChange={(e) => setHouseholdName(e.target.value)}
              />
              <button
                onClick={saveName}
                className="btn-secondary"
                disabled={savingName || householdName === household.name}
              >
                Save
              </button>
            </div>
          </div>

          <div>
            <label className="label">Invite code</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 font-mono text-lg tracking-widest dark:border-stone-700 dark:bg-stone-800 dark:text-stone-100">
                {household.invite_code}
              </code>
              <button onClick={copyCode} className="btn-secondary">
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
              Share this code with someone in your household. They can use it on
              the signup page to join.
            </p>
            <button
              onClick={rotate}
              className="mt-2 text-sm text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200"
              disabled={rotating}
            >
              {rotating ? "Rotating…" : "Generate a new code"}
            </button>
          </div>

          <div>
            <h3 className="label">Members</h3>
            <ul className="divide-y divide-stone-100 rounded-lg border border-stone-200 dark:divide-stone-800 dark:border-stone-700">
              {members.map((m) => (
                <li
                  key={m.id}
                  className="flex items-center justify-between px-3 py-2 text-sm"
                >
                  <div>
                    <p className="font-medium">{m.name}</p>
                    <p className="text-xs text-stone-500 dark:text-stone-400">{m.email}</p>
                  </div>
                  {m.id === user.id && (
                    <span className="text-xs text-stone-400 dark:text-stone-500">You</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {err && (
        <div className="card border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200">
          {err}
        </div>
      )}

      <button onClick={() => void logout()} className="btn-danger w-full">
        Sign out
      </button>
    </div>
  );
}

function ThemeIcon({ kind }: { kind: Theme }) {
  const className = "mx-auto h-5 w-5";
  if (kind === "light") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === "dark") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
      </svg>
    );
  }
  // "system" — laptop / device icon
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="12" rx="1" />
      <path d="M2 20h20" strokeLinecap="round" />
    </svg>
  );
}
